import conductionLogo from '@/assets/images/conduction-logo.png';
import MockActivityFeedPost from "@/components/conduction-social/ActivityFeedPost";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Utils } from "@/javascript/utils";
import {
    Button,
    Divider,
    Group,
    Stack,
    Text,
    Image
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconSend } from "@tabler/icons-react";
import React, { useCallback, useContext, useEffect } from "react";
import appsettings from "@/appsettings.json"
import ConductionModal from '../ConductionModal';

// Memorize published project IDs to avoid republishing during this session atleast (just to avoid easy spamming for now)
const publishedProjectIds = new Set<string>();

interface PublishProjectFormProps {
    onClose: () => void;
}

const PublishProjectForm: React.FC<PublishProjectFormProps> = ({ onClose }) => {
    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("PublishProjectForm must be a child of AuthContext.");
    }

    const [publishingState, setPublishingState] = React.useState<"Not Published" | "Publishing" | "Published">("Not Published");

    const navigateToActivityFeed = () => {
        // Open Conduction Social activity feed in new tab
        window.open(`${appsettings.ConductionSocialUrl}activity-feed`, "_blank");
    };

    const handlePublish = useCallback(async () => {
        if (auth.project === null || auth.user === null || auth.user.roles.includes("Guest") || auth.token === null) {
            Utils.displayNotification(
                "You must be logged in with an active project open in order to share to Conduction Social.",
                undefined,
                "blue",
                <IconAlertCircle size={16} />,
            );
            return;
        }

        if (auth.project.isDirty() && !auth.project.isLocked() && auth.project.userCanEdit(auth.user)) {
            await auth.saveProject();
        }

        setPublishingState("Publishing");
        const notificationId = Utils.displayNotification(
            "Publishing project...",
            "This may take a few minutes.",
            "blue",
            "loading"
        );

        try {
            const response = await ConductionAPI.publishProject(auth.project, auth.token);

            if (response.success) {
                Utils.updateNotification(
                    notificationId,
                    "Publish Complete",
                    <Button
                        onClick={navigateToActivityFeed}
                        leftSection={<Image src={conductionLogo} alt="Conduction Social" height={24} radius="sm" />}
                    >
                        <Text size="sm">View your project on Conduction Social</Text>
                    </Button>,
                    "green",
                    <IconCheck size={16} />,
                    true,
                    false
                );
                publishedProjectIds.add(auth.project.getId());
                setPublishingState("Published");
            } else {
                Utils.updateNotification(
                    notificationId,
                    response.message,
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true,
                    9000
                );
                setPublishingState("Not Published");
            }
        } catch (error) {
            console.error(error);
            Utils.updateNotification(
                notificationId,
                "Publish Failed",
                "An error occurred while publishing. Please try again later.",
                "red",
                <IconAlertCircle size={16} />,
                true
            );
            setPublishingState("Not Published");
        }
    }, [auth.project, auth.token, auth.user]);

    useEffect(() => {
        if (auth.project && publishedProjectIds.has(auth.project.getId())) {
            setPublishingState("Published");
        } else {
            setPublishingState("Not Published");
        }
    }, [auth.project, onClose]);

    return (
        <ConductionModal
            onClose={onClose}
            title={publishingState === "Published" ? "Your music has been shared on Conduction Social!" : "Share your project to Conduction Social and your friends will be able to listen to it!"}
            centered
            size="xl"
        >
            <Stack
                align="center"
                justify="center"
            >
                {auth.project != null && (
                    <Stack w="100%">
                        <Divider p="sm" w="100%" />
                        <Text>Here is a preview of the post:</Text>
                        <MockActivityFeedPost project={auth.project} />
                    </Stack>
                )}

                <Button
                    onClick={publishingState === "Published" ? navigateToActivityFeed : handlePublish}
                    loading={publishingState === "Publishing"}
                >
                    <Group>
                        <IconSend size={16} />
                        <Text>{publishingState === "Publishing" ? "Posting project..." : (publishingState === "Published" ? "View this project on Conduction Social." : "Post this project to Conduction Social!")}</Text>
                    </Group>
                </Button>
            </Stack>
        </ConductionModal>
    );
};

export default PublishProjectForm;